"use client";

import { useMemo, useState } from "react";
import { Check } from "lucide-react";
import { formatEurosLang, formatMinutes, jalonsPaiement, type VitrineLanguage } from "@/lib/vitrine/catalogue";
import { lineTotal, pricingTotals } from "@/lib/vitrine/pricing";
import { groupPricingByJalon, type JalonId } from "@/lib/vitrine/deroule";
import type { VitrinePricing } from "@/lib/vitrine/types";

// Grille tarifaire vitrine, au prix fixe (2026-10-12) : un seul prix en haut,
// puis les postes regroupés par jalon SANS prix par poste, les options à prix
// fixe, et le règlement en jalons 30/20/50 en bas. Le temps de travail reste
// affiché UNIQUEMENT au total (décision 2026-09-27 : avec la maintenance à
// 40 €/h affichée, des heures par ligne rendraient un taux calculable).
// Options cochables, total et jalons recalculés en direct. Rien n'est remonté
// au serveur.
// Ne reçoit jamais `generatedPrompt` (composant client : tout ce qu'il reçoit
// est sérialisé dans la page publique).

const COPY = {
  fr: {
    fixed: "Prix fixe",
    total: (t: string) => `${t} de travail au total. TVA non applicable, art. 293 B du CGI.`,
    jalon: "Jalon",
    options: "{t.options}",
    paid: "{t.paid}",
    pct: " %",
  },
  en: {
    fixed: "Fixed price",
    total: (t: string) => `${t} of work in total. VAT not applicable, article 293 B of the French General Tax Code.`,
    jalon: "Milestone",
    options: "Options, to add if you wish",
    paid: "Paid in three instalments, at each approved stage",
    pct: "%",
  },
} satisfies Record<VitrineLanguage, unknown>;

export function VitrinePricingGrid({
  pricing,
  jalons,
  lang = "fr",
}: {
  pricing: Omit<VitrinePricing, "generatedPrompt">;
  // Titres des jalons (déroulé), pour regrouper les postes (décision 2026-09-27).
  jalons: { id: JalonId; numero: number; titre: string; jours: string }[];
  lang?: VitrineLanguage;
}) {
  const t = COPY[lang];
  const eur = (n: number) => formatEurosLang(n, lang);
  const [options, setOptions] = useState<Set<string>>(new Set());
  const totals = useMemo(() => pricingTotals(pricing, options), [pricing, options]);
  const { groupes, options: opts } = useMemo(() => groupPricingByJalon(pricing), [pricing]);

  const toggle = (id: string) =>
    setOptions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="rounded-2xl border border-line bg-paper-pure">
      <div className="rounded-t-2xl bg-navy-deep p-5 text-center text-paper sm:p-6">
        <div className="font-mono text-xs uppercase tracking-widest text-gold">{t.fixed}</div>
        <div className="mt-1 font-display text-4xl sm:text-5xl">{eur(totals.montant)}</div>
        <p className="mt-2 text-sm text-paper/60">
          {t.total(formatMinutes(totals.minutes))}
        </p>
      </div>
      {groupes.map((g) => {
        const j = jalons.find((x) => x.id === g.id);
        return (
          <section key={g.id} className="border-b border-line last:border-b-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2 bg-paper-alt/70 px-4 py-3 sm:px-5">
              <div>
                <span className="font-mono text-xs uppercase tracking-widest text-gold">
                  {t.jalon} {j?.numero} · {j?.jours}
                </span>
                <div className="font-display text-xl text-ink">{j?.titre}</div>
              </div>
            </div>
            <ul className="divide-y divide-line">
              {g.lines.map((l) => (
                <li key={l.lotId} className="flex items-start gap-4 p-4 sm:gap-6 sm:px-5">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-ink">
                      {l.label}
                      {l.quantite > 1 && <span className="text-ink/60"> × {l.quantite}</span>}
                    </div>
                    <p className="mt-0.5 text-sm leading-relaxed text-ink/60">{l.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      {opts.length > 0 && (
        <div className="border-t border-line bg-paper-alt/60 p-4 sm:p-5">
          <p className="text-sm font-medium text-ink/70">Options, à ajouter si vous le souhaitez</p>
          <ul className="mt-3 space-y-2">
            {opts.map((l) => {
              const on = options.has(l.lotId);
              return (
                <li key={l.lotId}>
                  <button
                    type="button"
                    onClick={() => toggle(l.lotId)}
                    aria-pressed={on}
                    className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                      on ? "border-gold bg-paper-pure" : "border-line bg-paper-pure/60 hover:bg-paper-pure"
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                        on ? "border-gold bg-gold text-paper-pure" : "border-ink/30"
                      }`}
                    >
                      {on && <Check className="h-3.5 w-3.5" strokeWidth={2.5} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-ink">{l.label}</span>
                      <span className="block text-sm text-ink/60">{l.description}</span>
                    </span>
                    <span className="shrink-0 font-display text-ink">+ {eur(lineTotal(l))}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="rounded-b-2xl bg-navy-deep p-5 text-paper sm:p-6">
        <span className="text-paper/80">Réglé en trois fois, à chaque étape validée</span>
        <ul className="mt-4 grid gap-2 sm:grid-cols-3">
          {jalonsPaiement(pricing.catalogue, lang).map((j) => (
            <li key={j.part} className="rounded-lg border border-paper/15 p-3">
              <div className="font-display text-xl">{eur((totals.montant * j.part) / 100)}</div>
              <div className="text-xs text-paper/60">
                {j.part}
                {t.pct} · {j.label}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
